import {ERespFormat, IEndPoint, IO, IResponse} from "lib/commons/io/io";
import {JUserRolesMap} from "lib/commons/roles";
import {JPersistMetas, resPath} from "lib/store/res";
import {JNodeUtBrowser} from "back/store/views/utBrowser";

/** Fournit un accès à un UrlTree. */
export class UrlTreeSrv {

	naturalSortPathFn: (p1: resPath, p2: resPath) => number = URLTREE.defaultResPathSortFn;

	naturalSortNameFn: (n1: string, n2: string) => number = URLTREE.defaultResPathSortFn;

	naturalSortVersionFn: (v1: string, v2: string) => number = URLTREE.defaultResVersionSortFn;

	constructor(public url: IEndPoint, public anonymousAccount?: string) {}

	/**
	 * Retourne les nodeInfos d'une ressource.
	 * Rejet de la promesse : Error ou RespError
	 * @param directive
	 *   Restrictions des props ou persistMetas configurable parmi :
	 * 			&props=*&persistMetas=*
	 * 			&historyProps=*&historyPersistMetas=*
	 * 			&succProps=*&succPersistMetas=*
	 * 			&urlAncProps=*&urlAncPersistMetas=*
	 * 			&ancestorsProps=*&ancestorsPersistMetas=*
	 * 			...
	 * 		- Si le paramètre n'existe pas :
	 * 			-	toutes les props / persistMetas autorisées sont publiées
	 * 			- mais si le paramètre gloabel excludeAll est spécifié, aucune props / persistMetas n'est publiée
	 *		- Si le paramètre existe avec une valeur "", aucune props / persistMetas n'est publiée
	 *		- Sinon la liste du sous-ensemble des props / persistMetas à publier doit être définie séparée par des *
	 */
	nodeInfos(path: resPath, directive?: string): Promise<JNodeInfos | null> {
		return this.url.fetchJson<JNodeInfos>(URLTREE.path2RelativeUrl(path + (path.indexOf('?') < 0 ? "?nodeInfos" : "&nodeInfos") + (directive || "")));
	}

	/**
	 * Retourne un noeud avec ses fils.
	 * @param directive
	 *   Restrictions des props ou persistMetas configurable parmi :
	 * 			&props=*&persistMetas=*
	 * 			&childrenProps=*&childrenPersistMetas=*
	 * 		- Si le paramètre n'existe pas, toutes les props / persistMetas autorisées sont publiées
	 *		- Si le paramètre existe avec une valeur "", aucune props / persistMetas n'est publiée
	 *		- Sinon la liste du sous-ensemble des props / persistMetas à publier doit être définie séparée par des *
	 *	Options pour les children :
	 * 			&maxChildren=32 : nb max de fils publiés permettant de réduire la limite déjà configurée coté serveur
	 * 			&childrenFrom=chilNameIncluded&childrenTo=chilNameExcluded : sous-ensemble des fils
	 * 			&childrenAutoFold=250 : mécanisme de création de dossiers virtuels si le nb de fils dépasse cette valeur
	 */
	listChildren(path: resPath, directive?: string): Promise<JNodeInfos | null> {
		return this.url.fetchJson<JNodeInfos>(URLTREE.path2RelativeUrl(path + (path.indexOf('?') < 0 ? "?listChildren" : "&listChildren") + (directive || "")));
	}

	/**
	 * Recherche des noeuds dans la descendance de root.
	 * @param expression de recherche : nameStart(abc) & not(trashed) & not(unlisted)
	 */
	searchNodes(root: resPath, exp: string, max: number = 250, from?: resPath | undefined): Promise<JSearchNodesResult> {
		return this.url.fetchJson<JSearchNodesResult>(URLTREE.path2RelativeUrl(root + IO.query(root.indexOf('?') < 0, "searchNodes", null, "exp", exp, "max", max, "from", from)));
	}

	/**
	 * Retourne les infoViews d'une ressource.
	 * Rejet de la promesse : RespError
	 */
	infoViews(path: resPath): Promise<any> {
		return this.url.fetchJson<any>(URLTREE.path2RelativeUrl(path + (path.indexOf('?') < 0 ? "?V=infoViews" : "&V=infoViews")));
	}

	/**
	 * Retourne les JUserRolesMap d'une ressource via JNodeInfos.userRolesMap.
	 * Retourne aussi les props JNodeInfos.permaPath et JNodeInfos.resId (pour check de l'édition concurrente).
	 */
	async fetchUserRolesMap(path: resPath): Promise<JNodeInfos | null> {
		return await this.url.fetchJson<JNodeProps>(URLTREE.path2RelativeUrl(path + (path.indexOf('?') < 0 ? "?userRolesMap" : "&userRolesMap")));
	}

	/**
	 * Retourne les JUserRolesMap d'une ressource via JNodeInfos.userRolesMap.
	 * Retourne aussi les props JNodeInfos.permaPath et JNodeInfos.resId (pour check de l'édition concurrente).
	 */
	async fetchUserRolesMapForAccounts(path: resPath, pAccounts: string[]): Promise<JNodeInfos | null> {
		if (!pAccounts || pAccounts.length == 0) return null;
		return await this.url.fetchJson<JNodeProps>(URLTREE.path2RelativeUrl(path + IO.query(path.indexOf('?') < 0, "userRolesMap", null, "forUser", pAccounts.join("\t"))));
	}

	/**
	 * Interroge une ressource de l'urlTree.
	 */
	requestPath(path: resPath, directive?: string, format?: ERespFormat, init?: RequestInit): Promise<IResponse> {
		if (directive) path = path + (path.indexOf('?') < 0 ? "?" : "&") + directive;
		return this.url.fetch(URLTREE.path2RelativeUrl(path), format, init);
	}

	/**
	 * Construit une url pour un path donné.
	 */
	urlFromPath(path: resPath, directive?: string): string {
		if (directive) path = path + (path.indexOf('?') < 0 ? "?" : "&") + directive;
		return this.url.resolve(URLTREE.path2RelativeUrl(path)).url;
	}

	/**
	 * Requête sur le path avec la méthode HEAD.
	 * Les headers sont accessibles via IResponse.headers
	 * @param directve Fragment à ajouter à l'URL en querystring : "V=infoViews.json", "nodeInfos", tags, etc.
	 */
	headers(path: string, directive?: string): Promise<IResponse> {
		if (directive) path = path + (path.indexOf('?') < 0 ? "?" : "&") + directive;
		return this.url.fetch(URLTREE.path2RelativeUrl(path), 'none', {method: "HEAD"});
	}

	/**
	 * Extrait le sous-path si l'URL qui est descendant de la racine de l'urlTree.
	 * @return subPath qui n'est pas un resPath strict car
	 *  - pourrait contenir des directives en queryString n'appartenant pas au path.
	 *  - si on pointe la racine, retourne "/" ou /@x" et non "" ou "@x".
	 *  - si on est sur une racine versionnée, retourne /@x/abc" et non "@x/abc".
	 *
	 *  @see extractResPathFromUrl()
	 */
	extractSubPathFromUrl(url: string | null): string | null {
		const root = this.url.url;
		if (url && url.startsWith(root)) {
			if (root.charAt(root.length - 1) === '/') return url.substring(root.length - 1);
			if (url.charAt(root.length) === '/') return url.substring(root.length);
			if (url.length === root.length) return "/";
		}
		return null;
	}

	/**
	 * Extrait le resPath si l'URL qui est descendant de la racine de l'urlTree.
	 */
	extractResPathFromUrl(url: string | null): resPath | null {
		return URLTREE.extractResPathFromUrlPath(this.extractSubPathFromUrl(url));
	}
}


/** Propriétés disponibles d'un noeud de l'urlTree. */
export interface JNodeProps {

	/** Nom de la ressource : "x" ou "?x" */
	n?: string;

	/** Version de la ressource */
	v?: string;

	/** Index de ce noeud dans la chaine d'historique d'un noeud (0 étant le plus ancien). */
	idx?: number;

	/** Type de noeud. */
	t?: EUrlTreeNodeType;

	/**
	 * {@link IUrlTreeNode#getLayer()()}
	 */
	layer?: 'prod' | 'preprod' | 'qualif' | 'edit';

	/**
	 * {@link IUrlTreeNode#isFrozen()}
	 */
	frozen?: boolean;

	/**
	 * {@link IUrlTreeNode#isTrashed()}
	 */
	trashed?: boolean,

	/**
	 * {@link IUrlTreeNode#isUnlisted()}
	 */
	unlisted?: boolean,

	/**
	 * {@link IUrlTreeNode#getResId()}
	 */
	resId?: string,

	/**
	 * Chemin complet du node (/folder/file), tel que demandé dans la requête par le client.
	 * Attention, actuellement (5.0) ne respecte pas le type resPath :
	 * notemment path root = "/" et non "".
	 */
	path?: string, //NON resPath

	/**
	 * Path permanent complet du node (/folder@x/file@y).
	 */
	permaPath?: resPath,

	/** URL publique racine de l'urlTree (http://...). */
	rootUrl?: string,

	/** URL publique du node (http://...). */
	publicUrl?: string,

	/** URL publique permanente du node (http://...). */
	permaLink?: string;

	/** Extrait de {@link IUrlTreeNode#getFileRef()} lorsque le noeud est de type {@link EUrlTreeNodeType#repos}. */
	cdView?: string;

	/**
	 * URL cible d'un déplacement / renommage.
	 * Issu de {@link IUrlTreeNode#getFileRef()} lorsque le noeud est de type {@link EUrlTreeNodeType#moved}.
	 */
	movedRef?: string;

	/**
	 * Path cible d'un déplacement / renommage dans le même urlTree.
	 * Issu de {@link IUrlTreeNode#getFileRef()} lorsque le noeud est de type {@link EUrlTreeNodeType#moved}.
	 */
	movedPath?: string;

	/** Date de modification du noeud = date de modif du fichier correspondant à ResId.*/
	m?: number;

	/** Taille en octet de la ressource (FileRef) si un fichier est directeemnt pointé (nodeType==repos uniquement). */
	s?: number;

	/** Content-type du noeud. */
	ct?: string;

	/** Processing du noeud. */
	prc?: string;

	/**
	 * Map des user configurés sur ce noeud.
	 */
	userRolesMap?: JUserRolesMap;

	/**
	 * Rôles résolus pour le user en cours sur ce noeud.
	 */
	roles?: string[];

	/**
	 * Restriction d'accès sur le noeud.
	 */
	restrict?: string;

	/**
	 * Le noeud est-il head dans le noeud parent de contexte d'appel.
	 */
	isHead?: boolean
}

/** Sous-ensemble de propriétés utilisé pour manipuler les listes de ressources. */
export type JNodeShortProps = Pick<JNodeProps, 'n' | 'permaPath' | 'v' | 'idx' | 'm' | 't' | 'trashed' | 'unlisted' | 'prc' | 'resId' | 'roles'>;

export type ENodeProp = keyof JNodeProps;

/** Structure d'une requête de type ?nodeInfos. Toutes ces données ne sont pas systématiquement renseignées. */
export interface JNodeInfos extends JNodeProps {

	/** persistMetas du noeud.*/
	metas?: JPersistMetas

	/** nodeInfos des noeuds fils. */
	ch?: (JNodeInfos | JNodeAutoFold)[]

	/** Si la limite max du nb de fils a été dépassée. */
	moreCh?: boolean

	/** Noeuds ancêtres du node dans l'urlTree. */
	anc?: JNodeInfos[]

	/** Ancêtres du path par lequel on a atteint ce noeud lors de la requete. */
	urlAnc?: JNodeInfos[]

	/** nodeInfos des entrées d'historique de ce noeud. */
	hist?: JNodeInfos[]

	/** nodeInfos des successeurs de ce noeud. */
	succ?: JNodeInfos[]

	/** ce noeud matche-t-il le filtre qui a été passé à la requete */
	matchFilter?: boolean
}


export type ENodeInfo = keyof JNodeInfos;

/** Dossier virtuel "autoFold" pour gérer les très grandes liste de noeuds. */
export interface JNodeAutoFold {
	from: string
	to?: string
	ch: JNodeUtBrowser[] | null
}

export function isNodeAutoFold(d: JNodeShortProps | JNodeAutoFold): d is JNodeAutoFold {return d && ('from' in d)}


export type EUrlTreeNodeType =

/** Dossier sans contenu spécifié. Peut posséder des noeuds fils. */
	'noContent'

	/**
	 * Dossier avec une home de spécifiée et ses espaces de noms fils réservés {@link IUrlTreeNode#HOME_RESERVED_PATH} : "~~".
	 * Peut posséder des noeuds fils.
	 */
	| 'home'

	/**
	 * Dossier ou fichier, mais noeud terminal du point de vue l'arbre de gestion du dépot.
	 * Des noeuds fils peuvent exister mais ce sont des vues de leur noeud père construit par le système.
	 * Un fichier du repos (raw ou view de type metas ou content) lui est associé.
	 */
	| 'repos'

	/**
	 * Noeud terminal du point de vue l'arbre indiquant une redirection à retourner
	 * au client via la réponse Http "301 Moved permanently" ou "302 Moved Temporarily".
	 * Le {@link IUrlTreeNode#getFileRef()} est utilisé pour constituer la redirection.
	 * Si ce noeud contient des fils, ils ne seront jamais accédés via l'urlTree "front-office".
	 */
	| 'moved';


export interface JNodeTree extends JNodeShortProps {
	ch?: JNodeTree[] | null
}

export interface JSearchNodesResult {
	found: JNodeTree
	next?: resPath
}

export namespace URLTREE {


	/**
	 * Path racine si cette racine de l'urlTree n'est pas versionnée.
	 * Avec une racine de l'arbre versionnée, le racine est de la forme '@x',
	 */
	export const DEFAULT_PATH_ROOT = "";

	/**
	 * Retourne le nom du noeud terminal de ce resPath qu'il soit de la forme
	 * d'un queryString ("?x@x") ou d'un nom fils ("x@y").
	 */
	export function extractLeafName(resPath: resPath): string {
		if (resPath == null) return null;
		let i = resPath.lastIndexOf('?');
		if (i >= 0) return resPath.substring(i);
		i = resPath.lastIndexOf('/');
		return i >= 0 ? resPath.substring(i + 1) : resPath;
	}

	/**
	 * Retourne le nom du noeud terminal de ce resPath qu'il soit de la forme
	 * d'un queryString ("?x") ou d'un nom fils ("x").
	 * Correspond à JNodeProps.n
	 */
	export function extractUnversionedLeafName(resPath: resPath): string {
		if (resPath == null) return null;
		let i = resPath.lastIndexOf('?');
		if (i < 0) {
			i = resPath.lastIndexOf('/') + 1;
			if (i === 0) return ''; //cas d'un root '@x' donc name racine = ''.
		}
		let end = resPath.indexOf('@', i);
		return end >= 0 ? resPath.substring(i, end) : resPath.substring(i);
	}

	/** Retourne le path parent ou null si le path est un racine ("" ou "@x").*/
	export function extractParentPath(resPath: resPath): string | null {
		if (!resPath) return null;
		let i = resPath.lastIndexOf('?');
		if (i >= 0) return resPath.substring(0, i);
		i = resPath.lastIndexOf('/');
		return i >= 0 ? resPath.substring(0, i) : null;
	}

	/** Elimine la version du dernier fragment du path. */
	export function extractUnversionedLeafPath(resPath: resPath): resPath | null {
		if (resPath == null) return null;
		let i = resPath.lastIndexOf('?');
		if (i < 0) i = resPath.lastIndexOf('/');
		i = resPath.indexOf('@', i);
		return i >= 0 ? resPath.substring(0, i) : resPath;
	}

	/** Elimine la version de tous les fragments du path. */
	export function extractUnversionedPath(resPath: resPath): resPath | null {
		if (resPath == null) return null;
		return resPath.replace(/@[^\/]+/g, '');
	}

	/** Extrait la version du dernier fragment du path. */
	export function extractVersionLeaf(resPath: resPath): string | null {
		if (resPath == null) return null;
		let i = resPath.lastIndexOf('?');
		if (i < 0) i = resPath.lastIndexOf('/');
		i = resPath.indexOf('@', i + 1);
		return i >= 0 ? resPath.substring(i) : null;
	}

	/**
	 * @param newVersion si number, correspond à JNodeProps.idx dans la liste de l'historique.
	 */
	export function switchLeafVersionPath(resPath: resPath, newVersion: string | number): resPath | null {
		if (resPath == null) return null;
		let i = resPath.lastIndexOf('?');
		if (i < 0) i = resPath.lastIndexOf('/');
		i = resPath.indexOf('@', i);
		return (i >= 0 ? resPath.substring(0, i) : resPath) + '@' + (typeof newVersion === "number" ? "." + newVersion : newVersion);
	}


	export function buildPermaName(node: JNodeShortProps | null): string | null {
		if (node == null) return null;
		return node.v ? node.n + '@' + node.v : node.n;
	}

	/**  */
	export function appendToPath(path: resPath, subPath: string): resPath {
		if (!subPath || subPath === '.') return path;
		if (path == null) {
			//pas de folder parent
			if (!subPath) return URLTREE.DEFAULT_PATH_ROOT; //cas particulier root.
			path = "";
		}
		return subPath.charAt(0) === '?' ? path + subPath : path + '/' + subPath;
	}

	/** */
	export function appendLeafToPath(path: resPath, node: JNodeShortProps): resPath {
		if (path == null) return buildPermaName(node);
		return appendToPath(path, buildPermaName(node));
	}

	export function isRootPath(path: resPath): boolean {
		return path.indexOf('/') < 0 && path.lastIndexOf('?') < 0;
	}

	/** Ce resPath est-il de type Query, ie finissant par un noeud de type "?x". */
	export function isQueryPath(path: resPath): boolean {
		return path.lastIndexOf('?') >= 0;
	}

	/**
	 * ancestorPath est-elle un ancêtre de path.
	 * Si ancestorPath==path retourne false.
	 */
	export function isDescendantPath(ancestorPath: resPath, path: resPath): boolean {
		const c = path.charAt(ancestorPath.length);
		if (c !== '/' && c !== '?') return false;
		return path.startsWith(ancestorPath);
	}

	/**
	 * ancestorPath est-elle un ancêtre de path.
	 * Si ancestorPath==path retourne true.
	 * @param anyAncLeafVersion Si true, ancestorPath DOIT être un path unversionnedLeaf
	 */
	export function isDescendantPathOrEqual(ancestorPath: string, path: string, anyAncLeafVersion?: boolean): boolean {
		const c = path.charAt(ancestorPath.length);
		if (c === '/' || c === '?' || (anyAncLeafVersion && c === '@')) return path.startsWith(ancestorPath);
		return ancestorPath === path;
	}

	/**
	 * parentPath est-elle le path parent de path.
	 */
	export function isParentPath(parentPath: string, path: string): boolean {
		const parentLen = parentPath.length;
		if (path.length > parentLen && path.startsWith(parentPath)) {
			const c = path.charAt(parentLen);
			if (c === '?') return true;
			return c === "/" && path.indexOf("/", parentLen + 1) < 0 && path.indexOf("?", parentLen + 1) < 0;
		}
		return false;
	}


	/** Un resPath peut commencer par un '/', transforme ce path en relatif en éliminant ce '/'. */
	export function path2RelativeUrl(path: resPath): string {
		return path.charAt(0) === '/' ? path.substr(1) : path;
	}

	/**
	 * Extrait d'un fragment d'url commençant par "/" pour le transformer en resPath.
	 * - élimine le '/' de départ si on est sur le noeud racine de l'urlTree
	 * - élimine les directives n'appartenant pas au path.
	 *
	 * @see UrlTreeSrv.extractSubPathFromUrl()
	 */
	export function extractResPathFromUrlPath(urlPath: string | null): resPath | null {
		if (!urlPath) return urlPath // "" ou null
		if (urlPath.charAt(0) === '/') {
			if (urlPath.length === 1) return URLTREE.DEFAULT_PATH_ROOT;
			if (urlPath.charAt(1) === '@') urlPath = urlPath.substring(1); // /@x/abc -> @x/abc
		}
		const query = urlPath.indexOf('?');
		if (query > 0) {
			const qs = urlPath.substring(query);
			if (!qs.startsWith("?V=")) urlPath = urlPath.substring(0, query);
			else {
				const next = urlPath.indexOf('&', query);
				if (next > 0) urlPath = urlPath.substring(0, next);
			}
		}
		return urlPath as resPath;
	}

	/** Concat deux fragments d'url en gérant les différents cas de '/' en début et fin des fragments. */
	export function concatUrl(root: string, subPath: string) {
		if (subPath.charAt(0) === '/') {
			return root.charAt(root.length - 1) === '/' ? root + subPath.substr(1) : root + subPath;
		} else {
			return root.charAt(root.length - 1) === '/' ? root + subPath : root + "/" + subPath;
		}
	}


	export function isValidResName(resName: string): true | string {
		if (!resName) return "Un nom est requis.";
		//Caractères interdits.
		if (/[@+$\\\/><|!?:*#"~]/.test(resName)) return "Le nom ne doit comporter aucun des caractères suivants : @ $ + \\ / > < | ! ? : * # \" ~";
		//.. interdit
		if (/\.\./.test(resName)) return "Le nom ne doit pas comporter deux points successifs.";
		return true;
	}

	/** Normalise une string pour la rendre conforme avec {isValidResName} */
	export function filterResName(name: string): string {
		return name.replace(/[@+$\\\/><|!?:*#"~\s]/g, "");
	}

	export function defaultResSortFn(a: JNodeShortProps, b: JNodeShortProps): number {
		return defaultResPathSortFn(a.permaPath, b.permaPath);
	}

	export function defaultResPathSortFn(p1: resPath, p2: resPath): number {
		const s1 = p1.length;
		const s2 = p2.length;
		for (let i = 0, m = Math.min(s1, s2); i < m; i++) {
			const c1 = p1.codePointAt(i);
			const c2 = p2.codePointAt(i);
			if (c1 === c2) continue;
			if (c1 < 127) {
				if (c2 < 127) return URITREE_ASCII_ORDER[c1] - URITREE_ASCII_ORDER[c2];
				return -1;
			}
			return c1 - c2;
		}
		if (s1 === s2) return 0;
		return s1 - s2;
	}

	/**
	 * Tri des versions par défaut: algo de type "NDeci", N nombres séparés par un séparateur quelconque.
	 * Ordre descendant (plus grand en 1er) et version "" en 1er.
	 */
	export function defaultResVersionSortFn(v1: string, v2: string): number {
		if (!v1) return v2 ? -1 : 0;
		if (!v2) return 1;
		const n1 = v1.split(/\D+/);
		const n2 = v2.split(/\D+/);
		for (let i = 0; i < n1.length; i++) {
			if (i === n2.length) return -1;
			const d1 = Number.parseInt(n1[i]);
			const d2 = Number.parseInt(n2[i]);
			if (d1 !== d2) return d2 - d1;
		}
		return n1.length === n2.length ? 0 : 1;
	}
}

const URITREE_ASCII_ORDER = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 33, 32, 61, 62, 63, 64, 65, 66, 67,
	68, 69, 70, 48, 49, 50, 51, 52, 53, 54, 71, 73, 75, 77, 79, 81, 83, 85, 87, 89, 91, 93, 95, 97, 99, 101, 103, 105, 107, 109, 111, 113, 115, 117, 119, 121, 55, 56, 57, 58, 59, 60, 72, 74, 76, 78, 80, 82, 84, 86, 88, 90, 92, 94, 96, 98, 100, 102,
	104, 106, 108, 110, 112, 114, 116, 118, 120, 122, 123, 124, 125, 126];